import { IconSelector, IconX } from "@tabler/icons-react";
import clsx from "clsx";
import debounce from "lodash/debounce";
import React from "react";
import { useEffect } from "react";
import { type ClearIndicatorProps, type DropdownIndicatorProps, type MenuPlacement, type MultiValue, type OptionProps, components } from "react-select";
import AsyncSelect from "react-select/async";

type SelectOption = {
	value?: string | number | null;
	label: string;
};

type SelectProps<T> = {
	placeholder?: string;
	portal?: boolean;
	isMulti?: boolean;
	className?: string;
	clearable?: boolean;
	loadOptions: (inputValue: string) => Promise<T[]>;
	disabled?: boolean;
	options?: SelectOption[];
	minHeight?: number;
	maxHeight?: number;
	debounceMs?: number;
	onValueChange?: (values: MultiValue<SelectOption>) => void;
	defaultValue?: SelectOption["value"];
	closeMenuOnSelect?: boolean;
};

const ClearIndicator = (props: ClearIndicatorProps<SelectOption, false>) => (
	<components.ClearIndicator {...props}>
		<IconX size={16} stroke={1.5} />
	</components.ClearIndicator>
);

const DropdownIndicator = (props: DropdownIndicatorProps<SelectOption, false>) => (
	<components.DropdownIndicator {...props}>
		<IconSelector size={16} stroke={1.5} />
	</components.DropdownIndicator>
);

const Option = (props: OptionProps<SelectOption, false>) => <components.Option {...props} />;

const MultiValueRemove = (props: any) => (
	<components.MultiValueRemove {...props}>
		<IconX size={14} stroke={2} />
	</components.MultiValueRemove>
);

export default function TagsInput<T>({
	placeholder,
	closeMenuOnSelect = true,
	clearable = true,
	isMulti = false,
	minHeight = 300,
	maxHeight = 400,
	portal = true,
	debounceMs = 500,
	...props
}: SelectProps<T>) {
	const selectOptions = {
		defaultOptions: true,
		defaultValue: props.options ? props.options?.find((option) => option.value === props.defaultValue) : props.defaultValue,
		options: props.options || [],
		isClearable: clearable,
		isMulti,
		isDisabled: props.disabled,
		minMenuHeight: minHeight,
		maxMenuHeight: maxHeight,
		closeMenuOnSelect,
		menuPlacement: "auto" as MenuPlacement,
		onChange: (options: MultiValue<SelectOption>) => {
			// console.log(props.defaultValue);
			if (props.onValueChange) {
				props.onValueChange([...new Map(options.map((item) => [item.value, item])).values()]);
			}
		},
	};

	const handleLoadOptions = debounce((inputValue: string, callback: (options: T[]) => void) => {
		debounce(() => {
			if (props.loadOptions) {
				props
					.loadOptions(inputValue)
					.then((options) => {
						callback(options);
					})
					.catch((error) => {
						console.error(error);
						callback([]);
					});
			} else {
				callback([]);
			}
		}, debounceMs)();
	}, 500);

	return (
		<AsyncSelect
			loadOptions={handleLoadOptions}
			components={{
				ClearIndicator,
				DropdownIndicator,
				Option,
				MultiValueRemove,
			}}
			placeholder={placeholder}
			classNames={{
				control: ({ isDisabled }) =>
					clsx(
						"flex flex-row w-full min-h-[38px] py-[2px] pl-3 pr-0 gap-3 text-base !rounded-md", //max-h-[38px] h-[38px]
						isDisabled
							? "!bg-muted !text-muted-foreground !cursor-not-allowed"
							: "!bg-background !border-border focus-within:!border-focus-border focus-within:!ring-2 focus-within:!ring-focus-ring/30 !ring-offset-0 !outline-0",
					),
				valueContainer: () => "!p-0",
				placeholder: () => "!text-muted-foreground",
				menu: () => "!mt-1 !bg-background !border-1 !border-border !rounded-md !z-[90]",
				option: ({ isFocused, isSelected }) =>
					clsx(
						"!px-3 !py-2 !text-base !text-foreground !cursor-pointer",
						isSelected ? "!bg-primary-100 !text-foreground" : isFocused ? "!bg-primary-50" : "!text-foreground",
					),
				multiValue: () => "inline-flex items-center !h-[24px] !max-h-[24px] !p-1 !rounded !bg-slate-200/70 ",
				multiValueLabel: () => "!text-xs !text-slate-600 !font-semibold",
				multiValueRemove: () => "!text-xs !text-muted-foreground hover:!text-foreground !bg-transparent cursor-pointer",
				dropdownIndicator: () => "!text-muted-foreground",
				clearIndicator: () => "!text-muted-foreground !cursor-pointer",
			}}
			className={clsx("w-full text-base", props.className)}
			{...selectOptions}
		/>
	);
}
